import sha256 from "crypto-js/sha256";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./App.css";

const scrollToTop = () => {
  const scrollTarget =
    document.scrollingElement || document.documentElement || document.body;
  scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
};

const App: React.FC = () => {
  const backendUrl =
    "https://oss38xd89j.execute-api.eu-central-1.amazonaws.com";
  const [searchParams] = useSearchParams();
  const noteId = searchParams.get("id") ?? undefined;

  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [securityError, setSecurityError] = useState(false);
  const [peeked, setPeeked] = useState(false);
  const [noteExists, setNoteExists] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState<string | null>(null);
  const [securityAnswer, setSecurityAnswer] = useState("");

  const [newNote, setNewNote] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [useSecurity, setUseSecurity] = useState(false);
  const [createdUrl, setCreatedUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (
      isSubmitting ||
      createdUrl ||
      notFound ||
      securityError ||
      isLoading ||
      isRead
    ) {
      scrollToTop();
    }
  }, [isSubmitting, createdUrl, notFound, securityError, isLoading, isRead]);

  useEffect(() => {
    const peekNote = async (id: string) => {
      try {
        const response = await fetch(`${backendUrl}/nota/${id}/peek`);
        if (response.status === 200) {
          const data = await response.json();
          setNoteExists(true);
          if (data.securityQuestion) {
            setSecurityQuestion(data.securityQuestion);
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error("Error peeking note:", error);
        setNotFound(true);
      } finally {
        setPeeked(true);
      }
    };
    if (noteId) {
      peekNote(noteId);
    }
  }, [noteId]);

  const handleUnlock = async () => {
    if (!noteId) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsLoading(true);
    try {
      let securityAnswerHash = "";
      if (securityAnswer) {
        securityAnswerHash = sha256(
          securityAnswer.toLowerCase().trim()
        ).toString();
      }
      const response = await fetch(`${backendUrl}/nota/${noteId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-security-answer": securityAnswerHash,
        },
      });

      if (response.status === 404) {
        setNotFound(true);
      } else if (response.status === 403) {
        setSecurityError(true);
      } else {
        const data = await response.json();
        setSecurityError(false);
        setNotFound(false);
        setNote(data.note);
        setIsRead(true);
      }
    } catch (error) {
      console.error("Error fetching note:", error);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;
    if (useSecurity && (!newQuestion.trim() || !newAnswer.trim())) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsSubmitting(true);
    try {
      let securityAnswerHash = "";
      if (newAnswer) {
        securityAnswerHash = sha256(newAnswer.toLowerCase().trim()).toString();
      }
      const response = await fetch(`${backendUrl}/nota`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          note: newNote,
          securityQuestion: newQuestion || undefined,
          securityAnswer: securityAnswerHash || undefined,
        }),
      });
      const data = await response.json();
      setCreatedUrl(`${window.location.origin}/?id=${data.noteId}`);
    } catch (error) {
      console.error("Error creating note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      {noteId ? (
        <div className="note-view">
          {isLoading && <p>Caricamento...</p>}

          {!isLoading && notFound && (
            <div className="error-message">
              <h2>Nota non disponibile</h2>
              <p>La nota è già stata letta oppure il link non è valido.</p>
            </div>
          )}

          {!isLoading && securityError && (
            <div className="error-message">
              <h2>Risposta errata</h2>
              <p>La risposta alla domanda di sicurezza è errata.</p>
            </div>
          )}

          {!isLoading && peeked && noteExists && !isRead && (
            <div className="pre-message-container">
              <h2>Hai ricevuto una nota riservata</h2>
              <p>Attenzione: questa nota può essere letta solo una volta.</p>
              {securityQuestion && (
                <>
                  <p className="security-label">Domanda di sicurezza:</p>
                  <p className="security-question">{securityQuestion}?</p>
                  <input
                    type="text"
                    placeholder="Rispondi qui..."
                    value={securityAnswer}
                    onChange={(e) => setSecurityAnswer(e.target.value)}
                    className="text-input"
                  />
                </>
              )}
              <button
                className="submit-button"
                onClick={handleUnlock}
                disabled={securityQuestion ? !securityAnswer.trim() : false}
              >
                Apri nota riservata
              </button>
            </div>
          )}

          {isRead && (
            <div className="note-box">
              <h2>Hai ricevuto una nota riservata</h2>
              <p className="note-content">{note}</p>
              <p className="note-footer">
                Questa nota è stata cancellata dopo la lettura.
              </p>
              <br />
              <a
                href="/"
                className="submit-button"
                style={{ marginTop: "20px" }}
              >
                Crea una nuova nota
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="note-create">
          <h1>Crea una nota riservata</h1>
          <p>Il contenuto sarà leggibile una sola volta.</p>
          {!createdUrl ? (
            <>
              <textarea
                className="note-input"
                placeholder="Scrivi il tuo messaggio qui..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={useSecurity}
                  onChange={() => setUseSecurity(!useSecurity)}
                />
                Proteggi la nota con una domanda segreta
              </label>
              {useSecurity && (
                <>
                  <input
                    className="text-input"
                    placeholder="Domanda di sicurezza (obbligatoria)"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                  />
                  <input
                    className="text-input"
                    placeholder="Risposta alla domanda (obbligatoria)"
                    value={newAnswer}
                    onChange={(e) => setNewAnswer(e.target.value)}
                  />
                </>
              )}
              <button
                onClick={handleCreateNote}
                disabled={isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? "Generando link..." : "Genera link riservato"}
              </button>
            </>
          ) : (
            <div className="result-box">
              <p>Link generato:</p>
              <a href={createdUrl} target="_blank" rel="noopener noreferrer">
                {createdUrl}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default App;
